// The basic infantry: SRInfantryGob (full-damage bullet from the frame-1 firing point;
// SR's spread offsets the ABSOLUTE target) and LRInfantryGob (full-damage rocket from
// the current-frame launcher tip; LR's spread offsets the wdx/wdy DELTAS (sim-quirks A10) — target =
// m_wx+wdx). Both share the strip tables (firing 1-16, idle 17-32, moving 33-48), the
// 4-way idle pivot (1 sync-RNG draw), the %100+50 idle countdown, and the die-in-place
// "die 3" strip with a 1000-tick corpse fade (NO explosion gob — infantry don't blow).
// game/SRInfantry.cpp:1-145 + game/LRInfantry.cpp:1-137.

import type { SimContext } from '../context.ts';
import { MobileUnitGob } from './mobileunit.ts';
import { UnitGob } from './unitgob.ts';
import {
  MobileUnitConsts,
  InitMobileUnitConstsClass,
  kfUntcNotifyEnemyNearby,
  kwcTile16th,
} from './unitconsts.ts';
import { kgtShortRangeInfantry, kgtLongRangeInfantry } from './gobtypes.ts';
import { kutShortRangeInfantry, kutLongRangeInfantry, kupgmAdvancedHRC } from '../player/side.ts';
import {
  Message,
  knHandled,
  kmidReservedEnter,
  kmidReservedUpdate,
  kmidDelete,
  kstDying,
} from '../sm/message.ts';
import { kfAniResetWhenDone, kfAniIgnoreFirstAdvance } from '../anim/animation.ts';
import { WcFromPc32 } from '../math/coords.ts';
import { CreateBulletGob, CreateRocketGob } from './shots.ts';
import { kfGobLayerDepthSorted, kfGobLayerSurfaceDecal } from './gob.ts';
import { registerGobType } from './factory.ts';
import type { IniReader } from '../data/ini.ts';
import {
  ksfxNothing,
  ksfxMachineGunInfantryFire,
  ksfxRocketInfantryFire,
  ksfxRocketInfantryImpact,
  ksfxcInfantryDestroyed,
  ksfxcMale03Select,
  ksfxcMale03Move,
  ksfxcMale03Attack,
  ksfxcMajor02Select,
  ksfxcMajor02Move,
  ksfxcMajor02Attack,
} from '../data/soundeffects.ts';

// game/SRInfantry.cpp:14-16 (LRInfantry uses the same tables)
const s_anFiringStripIndices = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];
const s_anMovingStripIndices = [33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48];
const s_anIdleStripIndices = [17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32];

/** The shared infantry skeleton (identical bodies in both .cpp files). */
abstract class InfantryGobBase extends MobileUnitGob {
  // SR/LRInfantry.cpp Idle — 1/4 pivot left, 1/4 right, 1/2 idle in place (1 RNG draw)
  override Idle(): void {
    switch (this.ctx.rng.GetRandom() & 3) {
      case 0:
        this.m_dir--;
        if (this.m_dir < 0) this.m_dir = 15;
        this.StartAnimation(
          this.m_ani,
          this.m_pmuntc.anIdleStripIndices[this.m_dir],
          0,
          kfAniResetWhenDone,
        );
        break;
      case 1:
        this.m_dir++;
        if (this.m_dir > 15) this.m_dir = 0;
        this.StartAnimation(
          this.m_ani,
          this.m_pmuntc.anIdleStripIndices[this.m_dir],
          0,
          kfAniResetWhenDone,
        );
        break;
      default:
        this.StartAnimation(
          this.m_ani,
          this.m_pmuntc.anIdleStripIndices[this.m_dir],
          0,
          kfAniResetWhenDone,
        );
        break;
    }
  }

  // SR/LRInfantry.cpp GetIdleCountdown — somewhere between 8 & 12 seconds (1 RNG draw)
  override GetIdleCountdown(): number {
    return (this.ctx.rng.GetRandom() % 100) + 50;
  }

  // SR/LRInfantry.cpp kstDying — die in place ("die 3" strip), corpse fades at 1000 ticks
  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    if (st === kstDying) {
      switch (pmsg.mid) {
        case kmidReservedEnter: {
          // SR/LRInfantry.cpp:113-120 — Deactivate, then redraw this part of
          // the minimap (it will skip inactive munts)
          const trc = { left: 0, top: 0, right: 0, bottom: 0 };
          this.GetTileRect(trc);

          this.Deactivate();

          this.ctx.host.MiniMapRedrawTRect(trc.left, trc.top, trc.right, trc.bottom);

          this.m_ff = (this.m_ff ^ (kfGobLayerDepthSorted | kfGobLayerSurfaceDecal)) >>> 0;

          this.ctx.host.PlaySfx(`sfxc:${this.m_pmuntc.sfxcDestroyed}`);
          this.m_ani.StartByName('die 3', kfAniIgnoreFirstAdvance);
          this.MarkRedraw();

          this.ctx.smm!.SendDelayedMsgIds(kmidDelete, 1000, this.m_gid, this.m_gid);
          return knHandled;
        }

        case kmidReservedUpdate:
          this.AdvanceAnimation(this.m_ani);
          return knHandled;
      }
      // delegates kmidDelete + DiscardMsgs to MobileUnitGob's dying arms
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    return super.ProcessStateMachineMessage(st, pmsg);
  }
}

// game/SRInfantry.cpp:18-41
export function InitSRInfantryClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new MobileUnitConsts().bind(ctx);
  c.gt = kgtShortRangeInfantry;
  c.ut = kutShortRangeInfantry;
  c.wf |= kfUntcNotifyEnemyNearby;
  c.anFiringStripIndices = s_anFiringStripIndices;
  c.anMovingStripIndices = s_anMovingStripIndices;
  c.anIdleStripIndices = s_anIdleStripIndices;
  // Sound effects (SRInfantry.cpp:30-38)
  c.sfxFire = ksfxMachineGunInfantryFire;
  c.sfxImpact = ksfxNothing;
  c.sfxcDestroyed = ksfxcInfantryDestroyed;
  c.sfxcSelect = ksfxcMale03Select;
  c.sfxcMove = ksfxcMale03Move;
  c.sfxcAttack = ksfxcMale03Attack;
  return InitMobileUnitConstsClass(ctx, c, pini);
}

export class SRInfantryGob extends InfantryGobBase {
  constructor(ctx: SimContext) {
    // SRInfantry.cpp:48-50
    super(ctx, ctx.unitConsts[kutShortRangeInfantry] as MobileUnitConsts);
  }

  // game/SRInfantry.cpp:52-75 — bullet at FULL damage; spread on the ABSOLUTE target
  override Fire(
    puntTarget: UnitGob | null,
    wx: number,
    wy: number,
    wdx: number,
    wdy: number,
  ): boolean {
    if (!super.Fire(puntTarget, wx, wy, wdx, wdy)) return false;

    const wdxRnd = ((this.ctx.rng.GetRandom() & 7) - 3) * kwcTile16th;
    const wdyRnd = ((this.ctx.rng.GetRandom() & 7) - 3) * kwcTile16th;
    const ptSpecial32 = this.m_ani.GetSpecialPoint32(1); // frame 1 has the firing point

    CreateBulletGob(
      this.ctx,
      this.m_wx + WcFromPc32(ptSpecial32.x),
      this.m_wy + WcFromPc32(ptSpecial32.y),
      wx + wdxRnd,
      wy + wdyRnd,
      this.GetDamageTo(puntTarget!),
      this.m_gid,
      puntTarget!.GetId(),
    );

    this.ctx.host.PlaySfx(this.m_pmuntc.sfxFire);

    return true;
  }
}

// game/LRInfantry.cpp:13-37
export function InitLRInfantryClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new MobileUnitConsts().bind(ctx);
  c.gt = kgtLongRangeInfantry;
  c.ut = kutLongRangeInfantry;
  c.upgmPrerequisites = kupgmAdvancedHRC;
  c.wf |= kfUntcNotifyEnemyNearby;
  c.anFiringStripIndices = s_anFiringStripIndices;
  c.anMovingStripIndices = s_anMovingStripIndices;
  c.anIdleStripIndices = s_anIdleStripIndices;
  // Sound effects (LRInfantry.cpp:31-39)
  c.sfxFire = ksfxRocketInfantryFire;
  c.sfxImpact = ksfxRocketInfantryImpact;
  c.sfxcDestroyed = ksfxcInfantryDestroyed;
  c.sfxcSelect = ksfxcMajor02Select;
  c.sfxcMove = ksfxcMajor02Move;
  c.sfxcAttack = ksfxcMajor02Attack;
  return InitMobileUnitConstsClass(ctx, c, pini);
}

export class LRInfantryGob extends InfantryGobBase {
  constructor(ctx: SimContext) {
    // LRInfantry.cpp:39-41
    super(ctx, ctx.unitConsts[kutLongRangeInfantry] as MobileUnitConsts);
  }

  // game/LRInfantry.cpp:43-66 — rocket at FULL damage; spread on the wdx/wdy DELTAS
  // (the rocket's target = m_wx + wdx, NOT the absolute wx)
  override Fire(
    puntTarget: UnitGob | null,
    wx: number,
    wy: number,
    wdx: number,
    wdy: number,
  ): boolean {
    if (!super.Fire(puntTarget, wx, wy, wdx, wdy)) return false;

    wdx += ((this.ctx.rng.GetRandom() & 7) - 3) * kwcTile16th;
    wdy += ((this.ctx.rng.GetRandom() & 7) - 3) * kwcTile16th;
    const ptSpecial32 = this.m_ani.GetSpecialPoint32(); // the launcher tip (current frame)

    CreateRocketGob(
      this.ctx,
      this.m_wx + WcFromPc32(ptSpecial32.x),
      this.m_wy + WcFromPc32(ptSpecial32.y),
      this.m_wx + wdx,
      this.m_wy + wdy,
      this.GetDamageTo(puntTarget!),
      this.m_gid,
      puntTarget!.GetId(),
    );

    this.ctx.host.PlaySfx(this.m_pmuntc.sfxFire);

    return true;
  }
}

// self-register (see factory.ts)
registerGobType(kgtShortRangeInfantry, (ctx) => new SRInfantryGob(ctx));
registerGobType(kgtLongRangeInfantry, (ctx) => new LRInfantryGob(ctx));
